import { useQuery } from "@apollo/client";
import { SBErrorPubSub } from "@/utils/errors/SBError";
import { useEffect, useMemo } from "react";
import { BootcampPlaylistKind } from "@/schemaTypes";
import {
  BootcampPlaylistVideosDocument,
  BootcampPlaylistVideosQuery,
} from "./bootcampPlaylistVideos.generated";

export type PlaylistProvider =
  BootcampPlaylistVideosQuery["bootcampPlaylistVideos"]["provider"];

export interface VideoItem {
  provider: PlaylistProvider;
  id: string;
  title: string;
  description: string;
  thumbnailUrl: string;
  publishedAt: string;
  embedUrl: string;
}

export const useBootcampPlaylistVideos = (
  bootcampReleaseId: string | null | undefined,
  kind: BootcampPlaylistKind
) => {
  const { data, loading, error, refetch } = useQuery(
    BootcampPlaylistVideosDocument,
    {
      variables: {
        bootcampReleaseId: bootcampReleaseId ?? "",
        kind,
      },
      skip: !bootcampReleaseId,
      fetchPolicy: "no-cache",
    }
  );

  useEffect(() => {
    if (!error) return;
    SBErrorPubSub.publish({
      component: "useBootcampPlaylistVideos.tsx",
      message: error.message || "Error fetching bootcamp playlist videos",
      showInProd: true,
    });
  }, [error]);

  const videos: VideoItem[] = useMemo(() => {
    const raw = data?.bootcampPlaylistVideos?.videos ?? [];
    return raw
      .filter((v) => typeof v?.sourceId === "string" && v.sourceId.length > 0)
      .map((v) => ({
        provider: v.provider,
        id: v.sourceId,
        title: v.title ?? "",
        description: v.description ?? "",
        thumbnailUrl: v.thumbnailUrl ?? "",
        publishedAt:
          v.publishedAt instanceof Date
            ? v.publishedAt.toISOString()
            : v.publishedAt ?? "",
        embedUrl: v.embedUrl,
      }));
  }, [data]);

  return {
    videos,
    loading,
    error: Boolean(error),
    refetch,
    provider: data?.bootcampPlaylistVideos?.provider ?? null,
  };
};
